import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchPlaceCategory,
  updatePlaceCategory,
  fetchPlaceTypes,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function PlaceCategoryEditPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [placeTypes, setPlaceTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [form, setForm] = useState({
    placeCategoryName: "",
    placeCategoryDescription: "",
  });

  const loadData = useCallback(async () => {
    try {
      const [d, types] = await Promise.all([
        fetchPlaceCategory(categoryId),
        fetchPlaceTypes(businessDomainId),
      ]);
      setOriginal(d);
      setForm({
        placeCategoryName: d.placeCategoryName || "",
        placeCategoryDescription: d.placeCategoryDescription || "",
      });
      const allTypes = Array.isArray(types) ? types : [];
      setPlaceTypes(allTypes);
      const existing = Array.isArray(d.associatedPlaceTypes) ? d.associatedPlaceTypes : [];
      const existingIds = new Set(existing.map((a) => a.placeTypeId));
      setSelectedTypes(allTypes.filter((pt) => existingIds.has(pt.placeTypeId)));
    } catch (e) { notify.apiError(e); }
  }, [categoryId, businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypesChange = (e) => {
    const values = e.target.value;
    setSelectedTypes(typeof values === "string" ? [] : values);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    try {
      await updatePlaceCategory(categoryId, {
        ...original,
        ...form,
        associatedPlaceTypes: selectedTypes.map((pt) => ({
          placeTypeId: pt.placeTypeId,
          placeTypeName: pt.placeTypeName,
        })),
      });
      notify.success(t("message.update.success"));
      navigate(`/strategy/${businessDomainId}/place-categories/${categoryId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("placeCategory.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required
              label={t("placeCategory.name")}
              name="placeCategoryName"
              value={form.placeCategoryName}
              onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("placeCategory.placeTypes")}</InputLabel>
              <Select
                multiple
                value={selectedTypes}
                onChange={handleTypesChange}
                input={<OutlinedInput label={t("placeCategory.placeTypes")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v.placeTypeId} label={v.placeTypeName} size="small" />
                    ))}
                  </Box>
                )}
              >
                {placeTypes.map((pt) => (
                  <MenuItem key={pt.placeTypeId} value={pt}>
                    {pt.placeTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3}
              label={t("common.description")}
              name="placeCategoryDescription"
              value={form.placeCategoryDescription}
              onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/place-categories/${categoryId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
